import type { CostIndexLine, CostIndexPoint } from "./types";

/** Materials cycle the purple family; each non-material account keeps one fixed colour so it reads the same in every view. */
const MATERIAL = ["var(--color-ks-purple)", "var(--color-ks-purple-1)", "var(--color-ks-purple-2)", "var(--color-ks-purple-3)", "var(--color-ks-purple-shade)", "var(--color-ks-purple-4)"];
const ACCOUNT: Record<string, string> = {
  "632.1": "var(--color-ks-pass)", "627.4": "var(--color-ks-danger)", "627.2": "var(--color-ks-warn)",
  "627.1": "var(--color-ks-accent)", "628": "var(--color-ks-grey-1)", "629": "var(--color-ks-grey-2)",
};
const FALLBACK = ["var(--color-ks-grey-1)", "var(--color-ks-grey-2)", "var(--color-ks-grey-3)"];
export const OTHER_KEY = "other:small";

type PaletteLine = Pick<CostIndexLine, "kind" | "account"> & { key?: string };

export function lineColor<T extends PaletteLine>(line: T, index: number, colorFor?: (line: T) => string | undefined): string {
  const supplied = colorFor?.(line);
  if (supplied) return supplied;
  if (line.key === OTHER_KEY) return "var(--color-ks-grey-3)";
  if (line.kind === "material") return MATERIAL[index % MATERIAL.length];
  return ACCOUNT[line.account ?? ""] ?? FALLBACK[index % FALLBACK.length];
}

/** A carried point keeps its series identity while reading lighter than a measured point. */
export function carriedColor<T extends PaletteLine>(line: T, index: number, colorFor?: (line: T) => string | undefined): string {
  return `color-mix(in srgb, ${lineColor(line, index, colorFor)} 80%, var(--color-ks-grey-4))`;
}

/** Money chart only: a fully carried month's bars are 25 % more transparent than a production month's. */
export const CARRIED_FILL_OPACITY = 0.75;

/** Share of the latest total below which a material line is folded into "Other" (display grouping only; every value still comes from the response). */
export const FOLD_SHARE = 0.03;
export const MAX_SHOWN = 10;

/** Lines ordered by their latest unit cost, largest first, with the small material tail folded once there are many lines. */
export function rankLines(series: CostIndexPoint[]): { shown: CostIndexLine[]; folded: CostIndexLine[] } {
  const definitions = [...new Map(series.flatMap((point) => point.lines).map((line) => [line.key, line])).values()];
  const latest = series.at(-1);
  const value = (key: string) => Number(latest?.lines.find((line) => line.key === key)?.unit_cost ?? 0);
  const ordered = [...definitions].sort((a, b) => value(b.key) - value(a.key));
  const total = Number(latest?.total_unit_cost ?? 0);
  if (ordered.length <= 6 || !total) return { shown: ordered, folded: [] };
  const shown: CostIndexLine[] = []; const folded: CostIndexLine[] = [];
  for (const line of ordered) {
    const small = line.kind === "material" && (value(line.key) / total < FOLD_SHARE || shown.length >= MAX_SHOWN);
    (small ? folded : shown).push(line);
  }
  return folded.length === 1 ? { shown: ordered, folded: [] } : { shown, folded };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthShort = (month: string) => {
  const [year, m] = month.split("-");
  return `${MONTHS[Number(m) - 1] ?? m} '${year.slice(2)}`;
};
